import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  CheckCircle, XCircle, PaperPlaneTilt, Clock, Gauge, Thermometer, Snowflake, ChartBar
} from "@phosphor-icons/react";

/**
 * Freeze-Dryer Cycle-End reading — mobile-first, 3×3 grid for the 9 temperature
 * cells (TC + Ch1..Ch8), separate vacuum row, and a big "Approve & Notify"
 * button that ONLY appears when all thresholds are met.
 */
function timeSince(iso) {
  if (!iso) return null;
  const diffMin = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const h = Math.floor(diffMin / 60);
  return `${h}h ${diffMin % 60}m ago`;
}

function TempCell({ label, value, onChange, limit, unit = "°C", tone = "default", testId }) {
  const num = value === "" || value === null || value === undefined ? null : Number(value);
  const filled = typeof num === "number" && !Number.isNaN(num);
  const ok = filled && num <= limit;
  const bad = filled && !ok;

  const border = bad ? "border-red-400 ring-1 ring-red-200"
                : ok ? "border-emerald-400 ring-1 ring-emerald-200"
                : tone === "primary" ? "border-primary/40" : "border-border";
  const bg = bad ? "bg-red-50" : ok ? "bg-emerald-50" : "bg-card";

  return (
    <label className={`block rounded-xl border-2 ${border} ${bg} p-3 transition-colors`}>
      <div className="flex items-center justify-between mb-1">
        <span className={`text-[10px] font-bold uppercase tracking-wider ${tone === "primary" ? "text-primary" : "text-muted-foreground"}`}>
          {label}
        </span>
        {filled && (ok
          ? <CheckCircle size={14} weight="fill" className="text-emerald-600"/>
          : <XCircle size={14} weight="fill" className="text-red-600"/>
        )}
      </div>
      <Input
        value={value}
        onChange={e => onChange(e.target.value)}
        inputMode="decimal"
        placeholder={`≤ ${limit}`}
        data-testid={testId}
        className="mono text-2xl sm:text-3xl font-bold h-12 sm:h-14 px-1 text-center border-0 shadow-none bg-transparent focus-visible:ring-0 tabular-nums"
      />
      <div className="text-center text-[10px] text-muted-foreground mono mt-0.5">
        limit ≤ {limit}{unit}
      </div>
    </label>
  );
}

export default function FdReadingForm({ machine, activeBatch, onApproved }) {
  const bid = activeBatch?.id;
  const machineId = machine.id;

  const [thresholds, setThresholds] = useState(null);
  const [readings, setReadings] = useState([]);
  const [tc, setTc] = useState("");
  const [channels, setChannels] = useState(Array(8).fill(""));
  const [vac, setVac] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: t }, { data: rs }] = await Promise.all([
        api.get("/settings/fd-thresholds"),
        api.get(`/machines/${machineId}/fd-readings`, { params: { batch_id: bid, limit: 5 } }),
      ]);
      setThresholds(t);
      setReadings(rs);
    } catch {}
  }, [machineId, bid]);
  useEffect(() => { load(); }, [load]);

  const last = readings[0] || null;
  const lastSince = timeSince(last?.at);
  const remindEveryMin = thresholds?.reminder_interval_min || 30;
  const overdue = last ? (Date.now() - new Date(last.at).getTime()) / 60000 > remindEveryMin : true;

  const liveEval = useMemo(() => {
    if (!thresholds) return { ready: false, filled: false, failing: [] };
    const parse = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
    const tcV = parse(tc), vacV = parse(vac);
    const chs = channels.map(parse);
    const all = [tcV, ...chs, vacV];
    const filled = all.every(v => typeof v === "number" && !Number.isNaN(v));
    if (!filled) return { ready: false, filled: false, failing: [] };
    const failing = [];
    if (tcV > thresholds.temperature_controller_max_c) failing.push("TC");
    chs.forEach((v, i) => { if (v > thresholds.channel_max_c) failing.push(`Ch${i+1}`); });
    if (vacV > thresholds.vacuum_max_mbar) failing.push("Vac");
    return { ready: failing.length === 0, filled: true, failing };
  }, [thresholds, tc, channels, vac]);

  const canSubmit = liveEval.filled && !busy;
  const readyServerLast = !!last?.ready_to_end;
  const showApprove = liveEval.ready || readyServerLast;

  const submit = async () => {
    if (!liveEval.filled) { toast.error("Fill all 10 fields"); return; }
    setBusy(true);
    try {
      const body = {
        temperature_controller_c: Number(tc),
        channels: channels.map(Number),
        vacuum_mbar: Number(vac),
        note, batch_id: bid, batch_no: activeBatch?.batch_no,
      };
      const { data } = await api.post(`/machines/${machineId}/fd-readings`, body);
      toast.success(data.ready_to_end ? "Reading saved — cycle Ready to End" : "Reading saved");
      setNote("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const approveAndNotify = async () => {
    if (!bid) return;
    setBusy(true);
    try {
      const reading = {
        temperature_controller_c: Number(tc || last?.temperature_controller_c),
        channels: (tc && vac ? channels : last?.channels || []).map(Number),
        vacuum_mbar: Number(vac || last?.vacuum_mbar),
      };
      await api.post(`/batches/${bid}/complete-freeze-drying`, {
        reading, remarks: note || "Cycle ended — thresholds met",
      });
      toast.success("Cycle ended · batch queued for manager approval");
      setTc(""); setChannels(Array(8).fill("")); setVac(""); setNote("");
      onApproved && onApproved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not end cycle"); }
    finally { setBusy(false); }
  };

  if (!activeBatch) {
    return (
      <div className="border-t border-border pt-4 mt-4">
        <div className="overline mb-1">Cycle-End Reading</div>
        <div className="text-sm text-muted-foreground">No batch in Freeze Drying on this machine.</div>
      </div>
    );
  }

  if (!thresholds) {
    return <div className="border-t border-border pt-4 mt-4 text-sm text-muted-foreground">Loading form…</div>;
  }

  return (
    <div className="border-t border-border pt-5 mt-5" data-testid={`fd-reading-form-${machine.code}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="overline flex items-center gap-1"><Snowflake size={12}/>Cycle-End Reading</div>
          <div className="text-sm font-semibold mt-0.5">
            Batch <span className="mono">{activeBatch.batch_no}</span>
          </div>
          <div className="text-xs text-muted-foreground">{activeBatch.sku_name}</div>
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          {readyServerLast && (
            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"
                    data-testid={`fd-ready-badge-${machine.code}`}>
              <CheckCircle size={12} weight="fill" className="mr-1"/>Ready to End
            </Badge>
          )}
          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
            overdue ? "border-amber-300 bg-amber-50 text-amber-800" : "border-border bg-muted/40 text-muted-foreground"
          }`} data-testid={`fd-reminder-${machine.code}`}>
            <Clock size={10}/>{last ? `Last ${lastSince}` : "No reading"}
            {overdue && last && <span className="ml-0.5">· due</span>}
          </span>
        </div>
      </div>

      {/* 3×3 grid of temperature cells (TC + Ch1..Ch8) */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <TempCell label="TC" value={tc} onChange={setTc}
                   limit={thresholds.temperature_controller_max_c} tone="primary"
                   testId={`fd-tc-${machine.code}`}/>
        {[0,1,2,3,4,5,6,7].map(i => (
          <TempCell key={i} label={`Ch${i+1}`}
                     value={channels[i]}
                     onChange={(v) => setChannels(prev => prev.map((x, j) => j === i ? v : x))}
                     limit={thresholds.channel_max_c}
                     testId={`fd-ch${i+1}-${machine.code}`}/>
        ))}
      </div>

      {/* Vacuum row */}
      <div className="mb-3">
        <label className={`block rounded-xl border-2 p-3 transition-colors ${
          vac === "" ? "border-border bg-card"
          : Number(vac) <= thresholds.vacuum_max_mbar
            ? "border-emerald-400 bg-emerald-50 ring-1 ring-emerald-200"
            : "border-red-400 bg-red-50 ring-1 ring-red-200"
        }`}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-primary flex items-center gap-1">
              <Gauge size={11}/>Vacuum Level
            </span>
            {vac !== "" && (Number(vac) <= thresholds.vacuum_max_mbar
              ? <CheckCircle size={14} weight="fill" className="text-emerald-600"/>
              : <XCircle size={14} weight="fill" className="text-red-600"/>)}
          </div>
          <Input value={vac} onChange={e=>setVac(e.target.value)} placeholder={`≤ ${thresholds.vacuum_max_mbar}`}
                 inputMode="decimal" data-testid={`fd-vac-${machine.code}`}
                 className="mono text-2xl sm:text-3xl font-bold h-12 sm:h-14 px-1 text-center border-0 shadow-none bg-transparent focus-visible:ring-0 tabular-nums"/>
          <div className="text-center text-[10px] text-muted-foreground mono mt-0.5">
            limit ≤ {thresholds.vacuum_max_mbar} mbar
          </div>
        </label>
      </div>

      {/* Remarks */}
      <Input value={note} onChange={e=>setNote(e.target.value)} placeholder="Remarks (optional)"
             data-testid={`fd-note-${machine.code}`} className="h-11 text-sm mb-3"/>

      {/* Live status strip */}
      <div className={`text-sm font-semibold rounded-lg px-3 py-2 mb-3 flex items-center justify-between ${
        !liveEval.filled ? "bg-muted text-muted-foreground"
        : liveEval.ready ? "bg-emerald-100 text-emerald-800"
        : "bg-red-100 text-red-800"
      }`} data-testid={`fd-status-strip-${machine.code}`}>
        <span className="flex items-center gap-1.5">
          {liveEval.ready
            ? <><CheckCircle size={16} weight="fill"/>All thresholds met — Ready to End</>
            : !liveEval.filled
              ? <><ChartBar size={16}/>Fill all 10 fields for a live check</>
              : <><XCircle size={16} weight="fill"/>Failing: {liveEval.failing.join(", ")}</>
          }
        </span>
      </div>

      {/* CTA — big prominent Approve & Notify appears ONLY when ready */}
      {showApprove ? (
        <Button
          size="lg"
          className="w-full h-14 text-base font-bold shadow-md bg-emerald-600 hover:bg-emerald-700 text-white"
          onClick={approveAndNotify} disabled={busy}
          data-testid={`fd-approve-notify-${machine.code}`}
        >
          <PaperPlaneTilt size={20} weight="bold" className="mr-2"/>
          Approve &amp; Notify · End Cycle
        </Button>
      ) : (
        <Button
          size="lg"
          variant="outline"
          className="w-full h-12 text-sm font-semibold"
          onClick={submit} disabled={!canSubmit}
          data-testid={`fd-log-${machine.code}`}
        >
          Log Reading
        </Button>
      )}
      {showApprove && (
        <Button variant="ghost" size="sm" className="w-full mt-1 text-xs text-muted-foreground"
                onClick={submit} disabled={!canSubmit}
                data-testid={`fd-log-secondary-${machine.code}`}>
          Save reading only, don't end cycle
        </Button>
      )}

      {/* Recent readings mini-log */}
      {readings.length > 0 && (
        <div className="mt-4">
          <div className="overline mb-1.5">Recent readings</div>
          <div className="space-y-1 max-h-32 overflow-auto">
            {readings.slice(0, 5).map(r => (
              <div key={r.id} className="flex items-center justify-between text-[11px] px-2 py-1.5 rounded bg-muted/30"
                   data-testid={`fd-reading-row-${r.id}`}>
                <span className="mono text-muted-foreground">
                  {new Date(r.at).toLocaleTimeString([], {hour: "2-digit", minute: "2-digit"})}
                </span>
                <span className="mono text-xs">TC {r.temperature_controller_c}° · Vac {r.vacuum_mbar}</span>
                {r.ready_to_end
                  ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 h-4 px-1.5 text-[9px]">READY</Badge>
                  : <Badge variant="secondary" className="h-4 px-1.5 text-[9px]">HOLD</Badge>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
